import { MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Property } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';

interface PropertyCardProps {
  property: Property;
}

export function PropertyCard({ property }: PropertyCardProps) {
  const navigate = useNavigate();

  return (
    <Card
      className="group overflow-hidden bg-card border-border hover:border-accent transition-all duration-300 hover:shadow-lg hover:shadow-accent/20 cursor-pointer"
      onClick={() => navigate(`/property/${property.id}`)}
    >
      <CardHeader className="p-0 relative">
        <div className="aspect-[4/3] overflow-hidden">
          <img
            src={property.images[0]}
            alt={property.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        {property.badge && (
          <Badge className="absolute top-4 right-4 bg-accent text-accent-foreground font-medium">
            {property.badge}
          </Badge>
        )}
        <Badge
          variant="secondary"
          className="absolute top-4 left-4 bg-primary/90 text-primary-foreground backdrop-blur-sm"
        >
          {property.type}
        </Badge>
      </CardHeader>

      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-2 group-hover:text-accent transition-colors">
            {property.title}
          </h3>
          <p className="font-mono text-2xl font-bold text-accent">
            {property.price > 0 ? `$${property.price.toLocaleString()}` : 'Price on Request'}
          </p>
        </div>

        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="w-4 h-4" />
          <span className="text-sm">{property.location}</span>
        </div>

        <ul className="space-y-2">
          {property.features.slice(0, 3).map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="text-accent mt-0.5">•</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter className="p-6 pt-0">
        <Button
          className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
          onClick={(e) => { e.stopPropagation(); navigate(`/property/${property.id}`); }}
        >
          <MapPin className="w-4 h-4 mr-2" />
          View Details
        </Button>
      </CardFooter>
    </Card>
  );
}

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export function ServiceCard({ icon, title, description }: ServiceCardProps) {
  return (
    <Card className="bg-card border-border hover:border-accent/50 transition-all duration-300 hover:shadow-md">
      <CardContent className="p-6 space-y-4">
        <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center text-accent">
          {icon}
        </div>
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  );
}

interface VaultStepCardProps {
  step: number;
  title: string;
  description: string;
}

export function VaultStepCard({ step, title, description }: VaultStepCardProps) {
  return (
    <Card className="bg-primary border-accent/20 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full -translate-y-16 translate-x-16" />
      <CardContent className="p-6 space-y-4 relative">
        <div className="w-12 h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-xl">
          {step}
        </div>
        <h3 className="text-lg font-semibold text-primary-foreground">{title}</h3>
        <p className="text-sm text-primary-foreground/80 leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  );
}
